import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { getInactivityTimeout, hasFaceConfigured, hasPinConfigured } from '../services/securityService';
import { useAuth } from './AuthContext';

const SecurityContext = createContext(null);

// Minimum milliseconds between activity-event timer resets to avoid
// excessive cancellations on high-frequency events (e.g. mousemove, scroll).
const RESET_DEBOUNCE_MS = 2000;

/**
 * SecurityProvider — manages the inactivity lock.
 *
 * The app is locked automatically after the user-configured inactivity period.
 * Locking only activates when at least one secondary auth method (face or PIN)
 * is configured; otherwise there is no way to unlock.
 */
export const SecurityProvider = ({ children }) => {
  const { user } = useAuth();
  const [locked, setLocked] = useState(false);
  const [inactivityMs, setInactivityMs] = useState(300000); // default 5 min
  const timerRef = useRef(null);
  const lastResetRef = useRef(0);

  // Reload inactivity setting whenever the logged-in user changes
  useEffect(() => {
    if (user) {
      getInactivityTimeout().then(setInactivityMs);
    }
  }, [user]);

  /**
   * Reset (restart) the inactivity timer.
   * Called on every user interaction and after unlocking.
   * Debounced to avoid excessive restarts on high-frequency events.
   */
  const resetTimer = useCallback(() => {
    if (!user) return;

    const now = Date.now();
    if (now - lastResetRef.current < RESET_DEBOUNCE_MS) return;
    lastResetRef.current = now;

    if (timerRef.current) clearTimeout(timerRef.current);
    if (inactivityMs === 0) return; // 0 = never lock

    timerRef.current = setTimeout(async () => {
      // Only lock if the user has at least one unlock method configured
      const [face, pin] = await Promise.all([hasFaceConfigured(), hasPinConfigured()]);
      if (face || pin) {
        setLocked(true);
      }
    }, inactivityMs);
  }, [user, inactivityMs]);

  // Attach activity listeners while the user is logged in
  useEffect(() => {
    if (!user) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setLocked(false);
      return;
    }

    const events = ['mousemove', 'keydown', 'touchstart', 'click', 'scroll'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start timer on mount / login

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  /** Unlock the app and restart the inactivity timer. */
  const unlock = useCallback(() => {
    setLocked(false);
    lastResetRef.current = 0; // bypass debounce so timer restarts immediately
    resetTimer();
  }, [resetTimer]);

  /**
   * Re-read the inactivity timeout from storage (call after user changes the setting).
   */
  const refreshInactivityTimeout = useCallback(async () => {
    const ms = await getInactivityTimeout();
    setInactivityMs(ms);
  }, []);

  return (
    <SecurityContext.Provider value={{ locked, unlock, refreshInactivityTimeout }}>
      {children}
    </SecurityContext.Provider>
  );
};

export const useSecurity = () => {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error('useSecurity must be used within SecurityProvider');
  return ctx;
};

export default SecurityContext;
